/**
 * Transparency and colour surgery — BRAND MARKS ONLY (logos and icons).
 *
 * Deliberately kept out of ./imageCanvas.js: this is per-pixel work that only ever applies to
 * flat artwork. Photographic uploads must not reach for it — a JPEG has no alpha channel to
 * remove a background into, and chroma-keying a photograph produces garbage. Keeping it in its
 * own module is what lets a photo flow simply not import it.
 *
 * Every function here returns a PNG data URL because alpha must survive between steps.
 * Do NOT introduce a lossy intermediate in this file.
 */

import { loadImage } from './imageCanvas';

/**
 * Knock the background out to transparent by colour distance, with a feathered edge so the
 * result doesn't alias. `tolerance` is a straight RGB distance; the feather extends 40% beyond it.
 */
export const removeImageBackground = async (imageSrc, targetColor, tolerance = 30) => {
  const img = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  const { r: bgR, g: bgG, b: bgB } = targetColor;
  const tolSq = tolerance * tolerance;
  const featherRange = Math.max(tolerance * 0.4, 5);
  const featherSq = (tolerance + featherRange) * (tolerance + featherRange);

  for (let i = 0; i < data.length; i += 4) {
    const dr = data[i] - bgR;
    const dg = data[i + 1] - bgG;
    const db = data[i + 2] - bgB;
    const distSq = dr * dr + dg * dg + db * db;

    if (distSq <= tolSq) {
      data[i + 3] = 0;
    } else if (distSq <= featherSq) {
      const t = (Math.sqrt(distSq) - tolerance) / featherRange;
      data[i + 3] = Math.round(data[i + 3] * Math.min(1, t));
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return new Promise(resolve => {
    canvas.toBlob(blob => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    }, 'image/png');
  });
};

/**
 * Replace one color with another in an image, with tolerance and edge blending.
 */
export const replaceImageColor = async (imageSrc, sourceColor, targetColor, tolerance = 30) => {
  const img = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  const { r: srcR, g: srcG, b: srcB } = sourceColor;
  const { r: tgtR, g: tgtG, b: tgtB } = targetColor;
  const tolSq = tolerance * tolerance;
  const featherRange = Math.max(tolerance * 0.4, 5);
  const featherSq = (tolerance + featherRange) * (tolerance + featherRange);

  for (let i = 0; i < data.length; i += 4) {
    // Skip fully transparent pixels
    if (data[i + 3] === 0) continue;

    const dr = data[i] - srcR;
    const dg = data[i + 1] - srcG;
    const db = data[i + 2] - srcB;
    const distSq = dr * dr + dg * dg + db * db;

    if (distSq <= tolSq) {
      // Within tolerance — full replacement
      data[i] = tgtR;
      data[i + 1] = tgtG;
      data[i + 2] = tgtB;
    } else if (distSq <= featherSq) {
      // Feather zone — blend between original and target
      const t = (Math.sqrt(distSq) - tolerance) / featherRange;
      data[i] = Math.round(tgtR + (data[i] - tgtR) * t);
      data[i + 1] = Math.round(tgtG + (data[i + 1] - tgtG) * t);
      data[i + 2] = Math.round(tgtB + (data[i + 2] - tgtB) * t);
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return new Promise(resolve => {
    canvas.toBlob(blob => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    }, 'image/png');
  });
};

/** Pipette: read the colour at a relative (0..1) position. */
export const samplePixelColor = async (imageSrc, relX, relY) => {
  const img = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  const px = Math.min(Math.max(Math.round(relX * img.width), 0), img.width - 1);
  const py = Math.min(Math.max(Math.round(relY * img.height), 0), img.height - 1);
  const pixel = ctx.getImageData(px, py, 1, 1).data;
  return { r: pixel[0], g: pixel[1], b: pixel[2] };
};

/**
 * Check if an image has any transparent pixels (alpha < 255).
 *
 * Full edge scan plus a sparse interior sample. PictureUploadModal must NOT call this — it is a
 * whole-bitmap read answering a question that cannot matter for opaque output.
 */
export const checkHasTransparency = async (imageSrc) => {
  const img = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  // Sample edges + random interior pixels for speed
  const w = canvas.width;
  const h = canvas.height;
  // Check all edge pixels
  for (let x = 0; x < w; x++) {
    if (data[(0 * w + x) * 4 + 3] < 255) return true;         // top row
    if (data[((h - 1) * w + x) * 4 + 3] < 255) return true;   // bottom row
  }
  for (let y = 0; y < h; y++) {
    if (data[(y * w + 0) * 4 + 3] < 255) return true;         // left col
    if (data[(y * w + (w - 1)) * 4 + 3] < 255) return true;   // right col
  }
  // Sample interior (every 10th pixel)
  for (let i = 3; i < data.length; i += 40) {
    if (data[i] < 255) return true;
  }
  return false;
};

export const rgbToHex = ({ r, g, b }) => '#' + [r, g, b].map(c => c.toString(16).padStart(2, '0')).join('');
